/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2019 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import NodeTouchActionComponent from '../../../../../Library/Scripts/Utility/Component/Action/NodeTouchActionComponent';
import ComponentBase from '../../../../../Library/Scripts/Utility/ComponentBase';
import Decorators from '../../../../../Library/Scripts/Utility/Decorators';
import NodeTreeHelper from '../../../../../Library/Scripts/Utility/Node/NodeTreeHelper';
import TypeStrings from '../../../../../Library/Scripts/Utility/TypeStrings';

/** @hidden */
const property: typeof cc._decorator.property = cc._decorator.property;

@cc._decorator.ccclass
export default class ScrollViewDownloader extends NodeTouchActionComponent
{
    @property(cc.Float)
    protected maxWidth: number = 1500;

    @property(cc.Float)
    protected imageScale: number = 0.45;

    @property(cc.Vec2)
    protected documentScale: cc.Vec2 = new cc.Vec2(0.8, 0.8);

    @property
    protected symbolSpriteFrame: string = 'HUD_HelpPay_White';

    @property(cc.Float)
    protected widthScale: number = 0.55;

    @property(cc.Float)
    protected leftMarginPX: number = 100;

    private _scrollView: cc.ScrollView = null;
    private _container: cc.Node = null;

    private _documentBody: HTMLElement;
    private _nodeHelper: NodeTreeHelper;

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this._scrollView = ComponentBase.GetFirstParent(this.node, cc.ScrollView);
        this._container = this._scrollView.content;

        this._nodeHelper = new NodeTreeHelper(this._container);
    }

    protected action = (event?: any): void =>
    {
        if (CC_JSB
            || cc.sys.isNative
            || !this.isValidEvent(event)) { return; }

        this.resetBody();

        this._nodeHelper
                .TraverseDown(this._scrollView.node,
                [
                    this.spriteHandler,
                    this.labelHandler,
                    this.spineHandler
                ]);

        this.download();
        super.action(event);
    };

    private resetBody(): void
    {
        this._documentBody = document.createElement('div');
    }

    @Decorators.BindThis()
    private labelHandler(node: cc.Node): string
    {
        const label: cc.Label = node.getComponent(cc.Label);
        if (!label) { return; }

        this.appendBody(node, label.string);
    }

    @Decorators.BindThis()
    private spriteHandler(node: cc.Node): string
    {
        const sprite: cc.Sprite = node.getComponent(cc.Sprite);
        if (!sprite
            || sprite.type === cc.Sprite.Type.FILLED
            || sprite.type === cc.Sprite.Type.TILED) { return; }

        const frame: cc.SpriteFrame = sprite.spriteFrame;
        if (!frame) { return; }

        if (frame.name === this.symbolSpriteFrame)
        {
            this.appendSymbolElement(node);
            return;
        }

        const texture: cc.Texture2D = frame.getTexture();
        this.appendBody(node, this.getImg(node, texture, frame));
    }

    @Decorators.BindThis()
    private spineHandler(node: cc.Node): void
    {
        const spine: sp.Skeleton =  node.getComponent(sp.Skeleton);
        if (!spine || !spine.skeletonData) { return; }

        const spineName: string = spine.name.split('<')[0];
        const nameElement: HTMLSpanElement = document.createElement('span');
        nameElement.innerText = `[ ${spineName} ]`;
        nameElement.classList.add('img-title');

        this.appendBody(node, nameElement, 500);
    }

    private appendSymbolElement(node: cc.Node): void
    {
        const hexDigits: string = node.color.toHEX('#rrggbb');

        const square: string = '25px';
        const div: HTMLDivElement = document.createElement('div');
        div.classList.add('symbol');
        div.style.width = square;
        div.style.height = square;
        div.style.backgroundColor = `#${hexDigits}`;

        this.appendBody(node, div);
    }

    private getImg(node: cc.Node, texture: cc.Texture2D, frame: cc.SpriteFrame): HTMLImageElement
    {
        if (!texture || ! frame) { return; }
        const textureRect: cc.Rect = frame.getRect();

        const x: number = textureRect.center.x - (textureRect.width / 2);
        const y: number = textureRect.center.y - (textureRect.height / 2);

        const canvas: HTMLCanvasElement = document.createElement('canvas');
        const context: CanvasRenderingContext2D = canvas.getContext('2d');

        const scaleVec2: cc.Vec2 = this._nodeHelper.GetRelativeScale(node);
        scaleVec2.x *= this.imageScale;
        scaleVec2.y *= this.imageScale;

        const scaled: cc.Size = new cc.Size(textureRect.width * scaleVec2.x, textureRect.height * scaleVec2.y);

        canvas.width = scaled.width;
        canvas.height = scaled.height;

        context.drawImage(texture.getHtmlElementObj(),
            x,
            y,
            textureRect.width,
            textureRect.height,
            0,
            0,
            scaled.width,
            scaled.height);

        const data: string = canvas.toDataURL();

        const imgElement: HTMLImageElement = document.createElement('img');
        imgElement.src = data;
        imgElement.style.width = `${scaled.width}px`;
        imgElement.style.height = `${scaled.height}px`;
        return imgElement;
    }

    private download(): void
    {
        const htmlString: string = HTML_TEMPLATE.replace(BODY_TEMPLATE, this._documentBody.innerHTML);

        const element: HTMLAnchorElement = document.createElement('a');
        element.href = `data:text/plain;charset=utf-8,${encodeURIComponent(htmlString)}`;
        element.download = 'paytable.html';
        element.style.display = 'none';

        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }

    private getWrapper(node: cc.Node, content: HTMLElement | string, widthOverride: number = null): HTMLElement
    {
        let relativeX: number = this._nodeHelper.GetRelativeX(node);
        if (relativeX === 0)
        {
            relativeX = this.leftMarginPX;
        }
        else
        {
            relativeX += this._container.width / 2;
        }

        const left: number = relativeX * this.documentScale.x;
        const top: number = -this._nodeHelper.GetRelativeY(node) * this.documentScale.y;

        let width: number;
        if (widthOverride != null)
        {
            width = widthOverride;
        }
        else
        {
            width = Math.min(this._nodeHelper.GetRelativeWidth(node), this.maxWidth);
            width *= this.documentScale.x  * this.widthScale;
        }

        if (typeof(content) === TypeStrings.string)
        {
            const span: HTMLSpanElement = document.createElement('span');
            span.innerText = content as string;
            content = span;
        }

        const contentElement: HTMLElement = document.createElement('div');
        contentElement.appendChild(content as HTMLElement);
        contentElement.classList.add('content');

        const containerElement: HTMLElement = document.createElement('div');
        containerElement.appendChild(contentElement);
        containerElement.classList.add('node');
        containerElement.style.left = `${left}px`;
        containerElement.style.top = `${top}px`;
        containerElement.style.width = `${width}px`;

        return containerElement;
    }

    private appendBody(node: cc.Node, element: HTMLElement | string, width: number = null): void
    {
        element = this.getWrapper(node, element, width);
        this._documentBody.appendChild(element);
    }
}
const BODY_TEMPLATE: string = '{{body}}';

const STYLE_TEMPLATE: string =
`
div {
    display: inline-block;
    position: absolute;
}
div.node {
    padding: .5em;
}
img, .img-title {
    position: relative;
    left: -50%;
    top: -50%;
}
.symbol {
    margin: .2rem;
    border: 4px solid white;
}
`;

const HTML_TEMPLATE: string =
`
<!doctype html>
<html>
    <head>
        <style>${STYLE_TEMPLATE}</style>
    </head>
    <body>${BODY_TEMPLATE}</body>
</html>
`;
